/**
 * #769 — stale-bundle detection decision logic.
 *
 * A long-lived SPA session (desktop tab left open, or — the reported case — an
 * iOS Add-to-Home-Screen app that resumes a days-old JS snapshot) can keep
 * running old code indefinitely after a new build has deployed. `index.html`
 * is served `max-age=0, must-revalidate` and assets are hashed, so a genuine
 * *reload* always pulls the new bundle; the gap is purely that the running
 * client never reloads on its own.
 *
 * The client embeds its own build id (see build-id.ts, injected at build time)
 * and polls the server's build id from GET /api/health. When they diverge, a
 * new build is live and this pure function decides what to do about it.
 *
 * Kept dependency-free (no DOM, no globals) so it unit-tests in the Workers
 * pool and is the single source of truth for the reload-vs-toast call.
 */

export type VersionAction =
  /** Same build (or server id unknown) — do nothing. */
  | "none"
  /** New build live and the app is idle — safe to auto-reload silently. */
  | "reload"
  /** New build live but the app is busy — show a non-blocking toast instead. */
  | "toast";

export interface VersionDecisionInput {
  /** Build id baked into the running client bundle. */
  clientVersion: string;
  /**
   * Build id reported by the server right now. `null`/empty means the check
   * failed (offline, 5xx, malformed) — we must not act on an unknown.
   */
  serverVersion: string | null | undefined;
  /**
   * Is the app in a state where an auto-reload would destroy in-progress work?
   * (in-flight chat/stream, unsaved form input). When true we only ever toast.
   */
  busy: boolean;
  /**
   * Is the tab currently in the foreground (visible) when this check runs?
   * Akira's call (#777): a foreground tab is one the user *might* be mid-work
   * on even if nothing looks busy right now (form filled hours ago, called
   * away, tab still on screen). So a foreground tab ALWAYS gets the toast; we
   * only ever silently auto-reload a backgrounded tab — one nobody is looking
   * at, so a reload can't yank a page out from under anyone.
   */
  foreground: boolean;
  /**
   * #4832: the page runs with nobody at it (Display Mode on a floor screen).
   * A toast there waits for a tap that never comes, so an idle unattended page
   * reloads even in the foreground. Busy still only ever toasts.
   */
  unattended?: boolean;
  /**
   * #4836: the session behind that unattended page has lapsed (RequireAuth
   * is holding it, or the page's own polls are answered 401). A reload would
   * open the sign-in page with nobody there to sign in, so it toasts and the
   * page keeps its last render until the session is back. #4931: whether or
   * not the page is unattended at that moment: Display Mode held on a slide by
   * someone's step is not, and a reload there still opens sign-in. Only
   * Display Mode says its session has lapsed, so other pages are unchanged.
   */
  sessionLapsed?: boolean;
}

/**
 * Decide what to do given the running client's build id, the server's current
 * build id, whether the app is busy, and whether the tab is foregrounded.
 *
 * - same version → "none"
 * - server version unknown → "none" (never act on a failed/empty check)
 * - mismatch + backgrounded + idle → "reload" (safe: nobody's looking)
 * - mismatch + unattended + idle → "reload", foreground or not (#4832)
 * - mismatch + session lapsed → "toast", unattended or not (#4836, #4931)
 * - mismatch + (foreground OR busy) → "toast" (never reload out from under a
 *   user who's on the tab, or one with a chat/form in flight)
 */
export function decideVersionAction({
  clientVersion,
  serverVersion,
  busy,
  foreground,
  unattended = false,
  sessionLapsed = false,
}: VersionDecisionInput): VersionAction {
  // A failed/empty check must never trigger a reload or a scary toast.
  if (!serverVersion) return "none";
  // Defensive: an empty client id (shouldn't happen — build always injects one)
  // means we can't compare meaningfully, so stay put.
  if (!clientVersion) return "none";
  if (serverVersion === clientVersion) return "none";
  // Mismatch → a new build is live. Auto-reload ONLY a backgrounded, idle tab;
  // a foreground tab (or any busy one) gets the non-blocking toast instead.
  // #4832: an unattended page counts as backgrounded, since nobody is reading it.
  // #4836: never while the session has lapsed: the reload would open the
  // sign-in page. #4931: that holds whether or not the page is unattended.
  return busy || (foreground && !unattended) || sessionLapsed ? "toast" : "reload";
}
